import { LinkWithIconProps } from 'wds/linkWithIcon';
import { BadgeTooltip } from 'wds/popoverTooltip';
import type { OrgEdition } from '~/modules/services/userService';
import { i18nStore } from '~/modules/stores/i18nStore';
import { throwDevelopmentOrLog } from '~/modules/utilities/cross_env_utils';
import { getUniqueIteratorKey } from '~/modules/utilities/lwcUtilities';

export type BadgeKey = keyof I18nMessages['listingDetails']['badges'];

export enum BadgeVariant {
    Blue = 'blue',
    Purple = 'purple',
    Orange = 'orange',
    HotOrange = 'hot-orange',
    Gray = 'gray',
    Fuchsia = 'fuchsia',
    Indigo = 'indigo',
    Green = 'green',
    Yellow = 'yellow',
    Pink = 'pink',
}

export interface SimpleBadgeProps {
    value: string;
    variant?: BadgeVariant;
    iteratorKey?: string;
}

// Maps each org edition to an ax-badge theme (per Figma). Shared by the org cards that show a
// selected org (ace-header mobile card, ace-org-context-menu, wds/orgCard) so the mapping lives in
// one place. Unknown falls back to the neutral default so an unrecognized edition is never blank.
export const EDITION_BADGE_THEME: Record<OrgEdition, BadgeVariant> = {
    Production: BadgeVariant.Green,
    Sandbox: BadgeVariant.HotOrange,
    'Developer Edition': BadgeVariant.Yellow,
    'Scratch Org': BadgeVariant.Pink,
    // Dev Hub uses Blue (confirmed by UX); the "Dev Hub" label is the agreed name for now.
    'Dev Hub': BadgeVariant.Blue,
    Unknown: BadgeVariant.Gray,
};

// Resolves the ax-badge theme for an org edition, defaulting to Gray when the edition is missing or
// not a known key (e.g. a hand-edited/corrupt persisted cookie whose edition passes the string check
// in isOrgInfo but isn't a real OrgEdition) so the theme prop is never undefined.
export function getEditionBadgeTheme(edition: OrgEdition | undefined): BadgeVariant {
    return (edition ? EDITION_BADGE_THEME[edition] : undefined) ?? BadgeVariant.Gray;
}

export const BADGE_KEY_TO_VARIANT_MAP: Partial<Record<BadgeKey | 'Default', BadgeVariant>> = {
    isPledge1Percent: BadgeVariant.Indigo,
    isDiverseOwnedBusiness: BadgeVariant.Purple,
    salesforceMVP: BadgeVariant.Purple,
    topContributor: BadgeVariant.HotOrange,
    trailheadRank: BadgeVariant.Blue,
    Default: BadgeVariant.Gray,
};

export function generateSimpleBadge(value: string, variant?: BadgeVariant) {
    return {
        value: value || '',
        variant: variant || BADGE_KEY_TO_VARIANT_MAP.Default,
        iteratorKey: getUniqueIteratorKey(),
    };
}

export function generateLocalizedBadge(i18nKey: BadgeKey, i18nFn?: (args?: Record<string, string>) => string) {
    return {
        i18nFn: i18nFn ?? getBadgeI18nFunction(i18nKey),
        i18nKey,
        variant: BADGE_KEY_TO_VARIANT_MAP[i18nKey] || BADGE_KEY_TO_VARIANT_MAP.Default,
        iteratorKey: getUniqueIteratorKey(),
    };
}

export function getBadgeI18nFunction(badgeKey: BadgeKey): (args?: Record<string, string>) => string {
    const i18nFn = i18nStore.getState().i18n.listingDetails.badges[badgeKey];
    if (!i18nFn) {
        throwDevelopmentOrLog(
            new Error(`Failed to find i18n entry for badge '${badgeKey}' in i18n.listingDetails.badges.`),
        );
        return () => '';
    }
    if (badgeKey === 'numTopics' || badgeKey === 'numActions') {
        return i18nFn as () => string;
    }
    return i18nFn;
}

/**
 * Creates and adds an Agentforce badge to the badges array if count is positive
 * @param count - The numeric count to display
 * @param i18nKey - The i18n key for the label
 * @param i18nModule - The i18n module containing translations
 * @param badges - The badges array to append to
 */
export function createAgentforceBadge(
    count: number | undefined,
    i18nKey: 'actions' | 'agents' | 'mcpServers' | 'topics',
    i18nModule: I18nModule,
    badges: SimpleBadgeProps[],
): void {
    if (typeof count !== 'number' || isNaN(count) || count <= 0 || !i18nModule) {
        return;
    }
    let label: string;

    switch (i18nKey) {
        case 'actions': {
            label = i18nModule.oaf.agenticDetails.actions({
                numActions: count.toString(),
            });
            break;
        }
        case 'agents':
            label = i18nModule.oaf.agenticDetails.agents({
                numAgents: count.toString(),
            });
            break;
        case 'mcpServers':
            label = i18nModule.oaf.agenticDetails.mcpServers({
                numMCPServers: count.toString(),
            });
            break;
        case 'topics': {
            label = i18nModule.oaf.agenticDetails.topics({
                numTopics: count.toString(),
            });
            break;
        }
        default:
            label = `${i18nKey}: ${count}`;
    }

    badges.push(generateSimpleBadge(label));
}

// The Accessible Solution tooltip carries no link: its report is linked from the
// Compatibility section's Accessibility row instead.
type TooltipLinks = {
    isDiverseOwnedBusiness?: LinkWithIconProps;
};

function generateTooltipLinks(extension?: schema.oaf.Force_Listing): TooltipLinks {
    const tooltipLinks: TooltipLinks = {};

    if (extension?.isDiverseOwnedBusiness) {
        const diverseOwnedBusinessURL = extension.diverseOwnedBusinessURL;
        if (!diverseOwnedBusinessURL) {
            throwDevelopmentOrLog(new Error(`The URL for the diverse owned business is missing in extension.`));
        }
        tooltipLinks.isDiverseOwnedBusiness = {
            // @ts-expect-error * Automated Type Error Fix
            url: diverseOwnedBusinessURL,
            icon: 'new_window',
            caption: i18nStore.getState().i18nStaticCache.listingDetails.tooltipLinkCaptions.isDiverseOwnedBusiness,
        };
    }

    return tooltipLinks;
}

/**
 * Generates the tooltip rows based on the given features array, extension, and tooltip messages.
 *
 * @param badgeKeys The array of badges keys to generate the tooltips for.
 * @param extension The listing extension containing the link URLs.
 * @returns An array of BadgeTooltip, one for each badge key.
 */
export function generateBadgeTooltips(badgeKeys: BadgeKey[], extension?: schema.oaf.Force_Listing): BadgeTooltip[] {
    const tooltipLinks = generateTooltipLinks(extension);

    const { i18nStaticCache } = i18nStore.getState();

    return badgeKeys.map((key) => {
        // @ts-expect-error * Automated Type Error Fix
        const description = i18nStaticCache.listingDetails.tooltipsText[key];
        if (!description) {
            throwDevelopmentOrLog(new Error(`The description for badge ${key} is missing in the i18nStaticCache.`));
        }
        // @ts-expect-error * Automated Type Error Fix
        const link = tooltipLinks[key];
        return {
            badge: generateLocalizedBadge(key),
            description: (description ?? '') as string,
            link,
        };
    });
}
